/* @jsx h */
import { defineComponent, h } from "vue";
import { RightSheet } from "@lemmonhq/uikit";
import type { MenuCreatorProduct } from "../types";
import { NutritionalLabel } from "./NutritionalLabel";
import { useMenu } from "@app/vue/providers/index";

export interface ProductDetailModalProps {
  product: MenuCreatorProduct;
  isOpen: boolean;
  onClose: () => void;
}

export const ProductDetailModal = defineComponent<ProductDetailModalProps>({
  name: "ProductDetailModal",
  props: ["product", "isOpen", "onClose"],
  setup(props) {
    const { shop } = useMenu();
    if (!props.isOpen) return () => null;

    return () => (
      <RightSheet
        title={props.product.name}
        isOpen={props.isOpen}
        onClose={props.onClose}
        class="bg-base-200 h-full"
      >
        <div class="p-4">
          {/* Product Image */}
          {props.product.sample_image && (
            <div class="mb-6">
              <div class="aspect-[4/3] overflow-hidden rounded-xl">
                <img
                  src={props.product.sample_image}
                  alt={props.product.name}
                  class="w-full h-full object-cover"
                />
              </div>
            </div>
          )}

          {/* Product Information */}
          <div class="mb-6">
            <div class="flex items-start justify-between mb-4">
              <h2 class="font-serif text-2xl font-medium text-gray-900">
                {props.product.name}
              </h2>
              <span class="text-2xl font-semibold text-gray-900 ml-4">
                {shop.value.currency} {props.product.price}
              </span>
            </div>

            {props.product.description && (
              <p class="text-gray-600 mb-4 font-light leading-relaxed">
                {props.product.description}
              </p>
            )}

            <div class="flex items-center gap-3 mb-4">
              <span
                class={`px-3 py-1 rounded-full text-sm font-medium ${
                  props.product.item_type === "food"
                    ? "bg-green-100 text-green-700"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {props.product.item_type}
              </span>
              {props.product.weight && (
                <span class="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {props.product.weight}
                </span>
              )}
            </div>
          </div>

          {/* Nutritional Information */}
          {props.product.nutritional_info && (
            <div class="mb-6">
              <h3 class="font-serif text-xl font-medium text-gray-900 mb-4">
                Nutritional Information
              </h3>
              <div class="">
                <NutritionalLabel
                  nutrition={props.product.nutritional_info}
                  variant="full"
                />
              </div>
            </div>
          )}

          {/* Additional Product Details */}
          <div class="space-y-4">
            {props.product.nutritional_info?.allergens &&
              props.product.nutritional_info.allergens.length > 0 && (
                <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 class="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <span class="material-symbols-outlined text-sm">
                      warning
                    </span>
                    Allergen Information
                  </h4>
                  <p class="text-red-700 text-sm">
                    Contains:{" "}
                    {props.product.nutritional_info.allergens.join(", ")}
                  </p>
                </div>
              )}

            {props.product.nutritional_info?.dietaryTags &&
              props.product.nutritional_info.dietaryTags.length > 0 && (
                <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 class="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <span class="material-symbols-outlined text-sm">eco</span>
                    Dietary Information
                  </h4>
                  <div class="flex flex-wrap gap-2">
                    {props.product.nutritional_info.dietaryTags.map((tag) => (
                      <span
                        key={tag}
                        class="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
          </div>
        </div>
      </RightSheet>
    );
  },
});
